import React, { useState } from 'react';
import { PlusCircle, UserX, Users, RefreshCw, AlertTriangle } from 'lucide-react';
import { useClass } from '../context/ClassContext';
import { Class } from '../types';

export default function ClassForm() {
  const { currentClass, setCurrentClass, addStudent, removeStudent, clearClass } = useClass();
  const [className, setClassName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showForm, setShowForm] = useState(!currentClass);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleCreateClass = (e: React.FormEvent) => {
    e.preventDefault();
    const newClass: Class = {
      id: Date.now().toString(),
      name: className,
      date: new Date().toISOString().split('T')[0],
      students: []
    };
    setCurrentClass(newClass);
    setClassName('');
    setShowForm(false);
  };

  const handleAddStudent = (e: React.FormEvent) => {
    e.preventDefault();
    // Vérifier qu'au moins un des deux champs est rempli
    if (firstName.trim() || lastName.trim()) {
      addStudent(firstName.trim(), lastName.trim());
      setFirstName('');
      setLastName('');
    }
  };

  const handleReset = () => {
    setShowResetConfirm(true);
    setShowDeleteConfirm(false);
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
    setShowResetConfirm(false);
  };

  const confirmReset = () => {
    clearClass();
    setShowForm(true);
    setShowResetConfirm(false);
    localStorage.removeItem('currentClass');
  };

  const confirmDelete = () => {
    clearClass();
    setShowForm(true);
    setShowDeleteConfirm(false);
  };

  if (!showForm && currentClass) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        {showResetConfirm && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center mb-4">
              <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
              <span className="font-medium text-red-900">Confirmer la réinitialisation</span>
            </div>
            <p className="text-sm text-red-700 mb-4">
              Cette action supprimera toutes les données de la classe actuelle. Cette action est irréversible.
            </p>
            <div className="flex space-x-4">
              <button
                onClick={confirmReset}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Confirmer la réinitialisation
              </button>
              <button
                onClick={() => setShowResetConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Annuler
              </button>
            </div>
          </div>
        )}

        {showDeleteConfirm && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center mb-4">
              <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
              <span className="font-medium text-red-900">Confirmer la suppression</span>
            </div>
            <p className="text-sm text-red-700 mb-4">
              Cette action supprimera définitivement la classe et toutes ses données. Cette action est irréversible.
            </p>
            <div className="flex space-x-4">
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Confirmer la suppression
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Annuler
              </button>
            </div>
          </div>
        )}

        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{currentClass.name}</h2>
            <p className="text-sm text-gray-500">Date: {currentClass.date}</p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={handleReset}
              className="text-gray-600 hover:text-gray-800"
              title="Réinitialiser les données"
            >
              <RefreshCw className="h-5 w-5" />
            </button>
            <button
              onClick={handleDelete}
              className="text-red-600 hover:text-red-800"
              title="Supprimer la classe"
            >
              <UserX className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="mb-6">
          <form onSubmit={handleAddStudent} className="flex gap-4">
            <input
              type="text"
              placeholder="Prénom"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
            <input
              type="text"
              placeholder="Nom"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
            <button
              type="submit"
              disabled={!firstName.trim() && !lastName.trim()}
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                !firstName.trim() && !lastName.trim()
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              <PlusCircle className="h-5 w-5 mr-2" />
              Ajouter
            </button>
          </form>
          <p className="mt-2 text-sm text-gray-500">
            Remplissez au moins le prénom ou le nom
          </p>
        </div>

        <div className="overflow-hidden bg-white shadow sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {currentClass.students.map((student) => (
              <li key={student.id} className="px-4 py-4 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Users className="h-5 w-5 text-gray-400 mr-3" />
                    <p className="text-sm font-medium text-gray-900">
                      {student.firstName || student.lastName ? (
                        <>
                          {student.firstName} {student.lastName}
                        </>
                      ) : (
                        <span className="text-gray-500">Sans nom</span>
                      )}
                    </p>
                  </div>
                  <button
                    onClick={() => removeStudent(student.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <UserX className="h-5 w-5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Créer une nouvelle classe</h2>
      <form onSubmit={handleCreateClass}>
        <div className="mb-4">
          <label htmlFor="className" className="block text-sm font-medium text-gray-700">
            Nom de la classe
          </label>
          <input
            type="text"
            id="className"
            value={className}
            onChange={(e) => setClassName(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          />
        </div>
        <button
          type="submit"
          className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          <Users className="h-5 w-5 mr-2" />
          Créer la classe
        </button>
      </form>
    </div>
  );
}